import { JSONContent } from '@tiptap/core';
import { z } from 'zod';

export const noteCategories = ['assessment', 'plan', 'diagnosis', 'uncategorized'] as const;

export const NoteCategory = z.enum(noteCategories);

export const NoteOrder = z.number().nullable();

export const TiptapContent: z.ZodType<JSONContent> = z.lazy(() =>
  z
    .object({
      type: z.string().optional(),
      attrs: z.record(z.string(), z.any()).optional(),
      content: z.array(TiptapContent).optional(),
      marks: z
        .array(
          z
            .object({
              type: z.string(),
              attrs: z.record(z.string(), z.any()).optional()
            })
            .passthrough()
        )
        .optional(),
      text: z.string().optional()
    })
    .passthrough()
);

export const TiptapDocument = z.object({
  type: z.literal('doc'),
  content: z.array(TiptapContent)
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const NoteContent = (metaShape: z.ZodType<any> = z.any()) =>
  z.object({
    tiptap: TiptapDocument,
    version: z.string(),
    meta: metaShape.optional()
  });

export const NoteSource = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('node'),
    id: z.string().uuid()
  }),
  z.object({
    type: z.literal('edge'),
    id: z.string().uuid()
  })
]);

export const Note = z.object({
  /**
   * id: z.string().uuid(),
   * [RL] `id` is technically present but unused. We use `noteTemplateId` as
   * the primary key since noteTemplateId is the main way to associate notes
   * (especially pending notes) with note templates.
   */
  category: NoteCategory,
  content: NoteContent(),
  fileId: z.string(),
  moduleId: z.string(),
  noteTemplateId: z.string(),
  order: NoteOrder,
  pathwayFileId: z.string().nullable(),
  pathwayModuleId: z.string().nullable(),
  /** Launch session ids this note has participated in (empty = legacy / treat as prior lane). */
  sessions: z.array(z.string()).default([]),
  source: NoteSource,
  timeArchived: z.string().nullable(),
  timeCreated: z.string(),
  timeUpdated: z.string()
});

const NoteTemplateContentMeta = z.object({ ignoredOptionIds: z.array(z.string()).optional() });

export const NoteTemplate = z.object({
  id: z.string().uuid(),
  category: NoteCategory,
  content: NoteContent(NoteTemplateContentMeta),
  order: NoteOrder,
  source: NoteSource
});
